import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import seaTurtleSvg from "../assets/sea-turtle.svg";
import KoormaTortoise from "./KoormaTortoise";
import KoormaWorldMap from "./KoormaWorldMap";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { normalizeDirectionKey } from "./koormaUtils";

const RISK_LOADING_MESSAGES = [
  "Calculating planetary positions...",
  "Mapping Koorma Chakra directions...",
  "Analyzing celestial transits...",
  "Computing regional risk scores...",
  "Evaluating active triggers...",
  "Assessing conjunction influences...",
  "Building 60-day forecast...",
  "Generating risk report...",
];

function RiskLoadingOverlay({ estimatedTime = 45 }: { estimatedTime?: number }) {
  const [elapsed, setElapsed] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    setElapsed(0);
    const t = setInterval(() => setElapsed(p => p + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setMsgIdx(p => (p + 1) % RISK_LOADING_MESSAGES.length), 3500);
    return () => clearInterval(t);
  }, []);

  const progress = Math.min((elapsed / estimatedTime) * 100, 95);

  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-slate-950/80 backdrop-blur-sm">
      <div className="w-3/4 max-w-[200px] h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out"
          style={{
            width: `${progress}%`,
            background: "linear-gradient(90deg, #fb923c, #ea580c, #f97316)",
            backgroundSize: "200% 100%",
            animation: "shimmer 2s linear infinite",
          }}
        />
      </div>
      <p className="mt-3 text-xs text-slate-400 animate-pulse text-center px-2" key={msgIdx}>
        {RISK_LOADING_MESSAGES[msgIdx]}
      </p>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  );
}

type GlobalRiskDashboardProps = {
  data: any;
  title?: string;
  loading?: boolean;
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function toNumber(value: any, fallback = 0) {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getRiskColor(score: number) {
  if (score >= 50) return "#ef4444";
  if (score >= 30) return "#f59e0b";
  if (score >= 15) return "#10b981";
  return "#3b82f6";
}

function buildSparklinePoints(values: number[], width: number, height: number) {
  if (!values.length) return "";
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const span = Math.max(1e-6, maxV - minV);
  return values
    .map((v, i) => {
      const x = (i / Math.max(1, values.length - 1)) * width;
      const y = height - ((v - minV) / span) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export default function GlobalRiskDashboard({ data, title, loading = false }: GlobalRiskDashboardProps) {
  const { t } = useTranslation("chat");
  const [selectedDirection, setSelectedDirection] = useState<string | null>(null);

  const placeName = data?.location?.place_name || data?.location?.placeName || "";
  const rangeStart = data?.meta?.range?.start_date || data?.meta?.range?.startDate || "";
  const rangeEnd = data?.meta?.range?.end_date || data?.meta?.range?.endDate || "";
  const certainty = data?.certainty_level || data?.certaintyLevel || "";
  const directionNotice = data?.meta?.direction_notice || data?.meta?.directionNotice || "";
  const infoTooltipText =
    `${directionNotice ? `${directionNotice} ` : ""}Examples: Delhi → India-centered frame + Delhi-calculated transits/visibility. Tehran → India-centered frame + Tehran-calculated transits/visibility. Tokyo → India-centered frame + Tokyo-calculated transits/visibility.`;

  const dailyScores: number[] = useMemo(() => {
    const daily = Array.isArray(data?.daily) ? data.daily : [];
    return daily.map((d: any) => toNumber(d?.score, 0));
  }, [data]);

  const todayScore = dailyScores.length ? dailyScores[0] : 0;
  const todayScoreClamped = clamp(todayScore, 0, 100);
  const todayColor = getRiskColor(todayScoreClamped);

  const sparkSlice = dailyScores.slice(0, 60);
  const sparkPoints = useMemo(() => buildSparklinePoints(sparkSlice, 360, 64), [sparkSlice]);

  // Extract dates from the daily array for tick labels
  const dailyDates: string[] = useMemo(() => {
    const daily = Array.isArray(data?.daily) ? data.daily : [];
    return daily.slice(0, 60).map((d: any) => d?.date || "");
  }, [data]);

  // Build ~5 evenly-spaced tick marks (date + position)
  const sparkTicks = useMemo(() => {
    const count = sparkSlice.length;
    if (count < 2) return [];
    const tickCount = Math.min(5, count);
    const ticks: { x: number; label: string }[] = [];
    for (let i = 0; i < tickCount; i++) {
      const idx = i === tickCount - 1 ? count - 1 : Math.round((i / (tickCount - 1)) * (count - 1));
      const x = (idx / Math.max(1, count - 1)) * 360;
      const raw = dailyDates[idx] || "";
      // Format "2026-03-14" → "Mar 14"
      let label = raw;
      if (raw) {
        try {
          const d = new Date(raw + "T00:00:00");
          label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        } catch { /* keep raw */ }
      }
      ticks.push({ x, label });
    }
    return ticks;
  }, [sparkSlice, dailyDates]);

  // Min/max scores for Y-axis labels
  const sparkMin = sparkSlice.length ? Math.min(...sparkSlice) : 0;
  const sparkMax = sparkSlice.length ? Math.max(...sparkSlice) : 0;

  const contested = !!data?.regional_probability_model?.contested;
  const koormaTop = data?.regional_probability_model?.top_directions?.koorma;
  const namaTop = data?.regional_probability_model?.top_directions?.nama;

  const regions = useMemo(() => {
    const arr = Array.isArray(data?.regional_risks) ? data.regional_risks : [];
    return [...arr].sort((a: any, b: any) => toNumber(b?.risk_score_weighted, 0) - toNumber(a?.risk_score_weighted, 0));
  }, [data]);

  const triggers = Array.isArray(data?.active_triggers) ? data.active_triggers : [];

  const koormaAfflictions = useMemo(() => {
    const daily = Array.isArray(data?.daily) ? data.daily : [];
    const out: any[] = [];
    for (const d of daily) {
      const aff = d?.details?.koorma_afflictions;
      if (Array.isArray(aff)) out.push(...aff);
    }
    return out;
  }, [data]);

  const selectedRegion = useMemo(() => {
    if (!selectedDirection) return null;
    const key = normalizeDirectionKey(selectedDirection);
    return regions.find((r: any) => normalizeDirectionKey(r?.koorma_direction || "") === key) || null;
  }, [regions, selectedDirection]);

  return (
    <div className="w-full overflow-x-hidden rounded-xl border border-yellow-500/20 bg-slate-950/60 text-slate-100" role="region" aria-label={t("chat.global_risk.dashboard_title")}>
      <div className="flex flex-col gap-2 border-b border-yellow-500/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg p-1.5" style={{ backgroundColor: '#ffffff' }} aria-hidden="true">
            <img src={seaTurtleSvg} alt="" className="h-full w-full" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-yellow-400">{title || t("chat.global_risk.dashboard_title")}</div>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                      aria-label={t("chat.global_risk.reference_frame_info", { defaultValue: "About direction reference frame" })}
                      title={infoTooltipText}
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="z-[9999] max-w-[360px] text-xs leading-relaxed">
                    {infoTooltipText}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {placeName && <div className="text-xs text-slate-400">{placeName}</div>}
          </div>
        </div>
        <div className="text-xs text-slate-400">
          {rangeStart && rangeEnd ? (
            <span>
              {rangeStart} → {rangeEnd}
            </span>
          ) : (
            <span />
          )}
          {certainty ? <span className="ml-2 rounded-md bg-slate-900 px-2 py-1 text-[11px] text-slate-300">{t("chat.global_risk.certainty")}: {certainty}</span> : null}
        </div>
      </div>

      {contested ? (
        <div className="mx-4 mt-3 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" role="alert">
          {t("chat.global_risk.method_disagreement")}: Koorma → <span className="font-semibold">{String(koormaTop || "—")}</span>, Nama →{" "}
          <span className="font-semibold">{String(namaTop || "—")}</span>
        </div>
      ) : null}

      <div className="grid gap-4 p-4 lg:grid-cols-3">
        <div className="relative min-w-0 overflow-hidden rounded-xl border border-yellow-500/15 bg-slate-950/70 p-4" aria-label={t("chat.global_risk.today_risk_score")}>
          {loading && <RiskLoadingOverlay />}
          <div className="text-xs text-slate-400">{t("chat.global_risk.today_risk_score")}</div>
          <div className="mt-2 flex items-end justify-between">
            <div className="text-4xl font-bold" style={{ color: todayColor }} aria-live="polite">
              {todayScoreClamped.toFixed(1)}
            </div>
            <div className="text-xs text-slate-400">{t("chat.global_risk.score_range")}</div>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-800" role="progressbar" aria-valuenow={todayScoreClamped} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full" style={{ width: `${todayScoreClamped}%`, background: todayColor }} />
          </div>
          <div className="mt-2 text-[10px] text-slate-500 leading-tight">
            * Based on global planetary positions. Score varies mainly during eclipses and Sankranti transitions visible from the selected location.
          </div>
          <div className="mt-4">
            <div className="text-xs text-slate-400">{t("chat.global_risk.next_60_days")}</div>
            <div className="mt-1 text-[10px] text-slate-500">Tick labels show the date range covered by this trend.</div>
            <div className="relative mt-2">
              {/* Y-axis min/max labels */}
              {sparkSlice.length > 1 && sparkMax > sparkMin && (
                <div className="pointer-events-none absolute bottom-0 left-0 top-0 hidden flex-col justify-between text-[9px] text-slate-500 sm:flex" style={{ width: 0 }}>
                  <span className="relative -top-1 -left-7">{sparkMax.toFixed(0)}</span>
                  <span className="relative -bottom-1 -left-7">{sparkMin.toFixed(0)}</span>
                </div>
              )}
              <svg width="100%" height="64" viewBox="0 0 360 64" className="block" aria-label={t("chat.global_risk.next_60_days")}>
                <polyline points={sparkPoints} fill="none" stroke={todayColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              {/* Date tick labels below the chart */}
              {sparkTicks.length > 0 && (
                <div className="flex justify-between text-[9px] text-slate-500 mt-0.5 px-0">
                  {sparkTicks.map((tick, i) => (
                    <span key={i} className={i === 0 ? "text-left" : i === sparkTicks.length - 1 ? "text-right" : "text-center"}>
                      {tick.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative min-w-0 overflow-hidden rounded-xl border border-yellow-500/15 bg-slate-950/70 p-4" aria-label={t("chat.global_risk.top_regions")}>
          {loading && <RiskLoadingOverlay />}
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-slate-400">{t("chat.global_risk.top_regions")} <span className="text-[10px] text-slate-500" title="Weighted directional risk score (0–100). Higher = more planetary stress on this sector.">(weighted)</span></div>
              <div className="mt-1 text-[10px] text-slate-500">Node values show weighted sector risk scores on a 0–100 scale.</div>
            </div>
            {selectedDirection ? <div className="min-w-0 break-words text-right text-[11px] text-slate-400">{String(selectedDirection)}</div> : null}
          </div>
          <div className="mt-3">
            <KoormaTortoise risks={regions} selectedDirection={selectedDirection} onSelectDirection={setSelectedDirection} />
          </div>
          {selectedRegion ? (
            <div className="mt-4 min-w-0 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 break-words text-xs font-semibold text-slate-200">{String(selectedRegion?.koorma_direction || "—")}</div>
                <div className="shrink-0 text-xs font-semibold" style={{ color: getRiskColor(clamp(toNumber(selectedRegion?.risk_score_weighted, 0), 0, 100)) }} title="Weighted risk score: combined Koorma + Nama directional stress (0–100)">
                  {clamp(toNumber(selectedRegion?.risk_score_weighted, 0), 0, 100).toFixed(1)}
                </div>
              </div>
              {selectedRegion?.primary_threat ? <div className="mt-1 break-words text-[11px] text-yellow-300/90">{String(selectedRegion.primary_threat)}</div> : null}
              {Array.isArray(selectedRegion?.top_industries) && selectedRegion.top_industries.length ? (
                <div className="mt-1 break-words text-[11px] text-slate-400">{selectedRegion.top_industries.join(", ")}</div>
              ) : null}
              {selectedRegion?.probability != null ? (
                <div className="mt-2 text-[11px] text-slate-500" title="This direction's share of the total model risk weight (all directions sum to ~1.0)">Directional share: {(toNumber(selectedRegion.probability, 0) * 100).toFixed(1)}%</div>
              ) : null}
              <button
                type="button"
                className="mt-2 text-[11px] text-yellow-400 hover:text-yellow-300 transition-colors"
                onClick={() => document.getElementById("global-risk-report")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                View full report section ↓
              </button>
            </div>
          ) : (
            <div className="mt-4 text-xs italic text-slate-500">Select a direction node to view the sector score, mapped regions, and current risk details.</div>
          )}
        </div>

        <div className="relative min-w-0 overflow-hidden rounded-xl border border-yellow-500/15 bg-slate-950/70 p-4" aria-label={t("chat.global_risk.active_triggers")}>
          {loading && <RiskLoadingOverlay />}
          <div className="text-xs text-slate-400">{t("chat.global_risk.active_triggers")}</div>
          <div className="mt-3 space-y-2">
            {triggers.length ? (
              triggers.slice(0, 8).map((tr: any, idx: number) => (
                <div key={idx} className="min-w-0 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{t("chat.global_risk.trigger_type")}</div>
                  <div className="mt-1 break-words text-xs font-semibold text-slate-200">{String(tr?.type || t("chat.global_risk.trigger_type")).replace(/_/g, " ")}</div>
                  {tr?.details ? (
                    <div className="mt-2">
                      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Details</div>
                      <div className="mt-1 break-words text-[11px] text-slate-400">{String(tr.details)}</div>
                    </div>
                  ) : null}
                  {tr?.prediction ? (
                    <div className="mt-2">
                      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                        {typeof tr.prediction === "string" && /^\d{4}-\d{2}-\d{2}T/.test(tr.prediction) ? "Peak Date" : "Watch"}
                      </div>
                      <div className="mt-1 break-words text-[11px] text-yellow-300/90">{String(tr.prediction)}</div>
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-xs text-slate-500">{t("chat.global_risk.no_triggers")}</div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4">
        <KoormaWorldMap risks={regions} koormaAfflictions={koormaAfflictions} />
      </div>
    </div>
  );
}
